//Main Component thathandle frontdesk ad doctors view

import React, { useState, useEffect } from "react";
import { Box, useToast } from "components-lib";
import { useLocation } from "react-router-dom";
import { axiosInstance } from "config/api";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { columnsDataComplex } from "views/admin/default/variables/columnsData";
import PatientDetails from "./PatientDetails";
import AppointmentTablePage from "./AppointmentTablePage";
import BillsPage from "../bills/index";
import ModernReports from "../reports/index";
import BookAppointmentModal from "views/admin/default/components/BookAppointmentModal";

// Socket provider (single shared socket connection)
import { SocketProvider } from "context/SocketProvider";

// Import modular components
import HeaderControls from "./components/sections/HeaderControls";
import ConsultationView from "./components/sections/ConsultationView";
import EditDoctorModal from "./components/modals/EditDoctorModal";

// Import custom hooks
import { useUserRole } from "./components/hooks/useUserRole";
import { usePatientData } from "./components/hooks/usePatientData";
import { useConsultationData } from "./components/hooks/useConsultationData";
import { useAppointmentData } from "./components/hooks/useAppointmentData";
import { useDoctorsList } from "./components/hooks/useDoctorsList";
import { useDoctorManagement } from "./components/hooks/useDoctorManagement";
import { useAppointmentBooking } from "./components/hooks/useAppointmentBooking";
// Global unified filters
import {
    GlobalFiltersProvider,
    useGlobalFilters,
} from "contexts/GlobalFiltersContext";
import GlobalFiltersBar from "components/GlobalFilters/GlobalFiltersBar";

/**
 * UserReports Component - Main Doctor Dashboard
 *
 * This component has been modularized into smaller, focused components and hooks:
 *
 * COMPONENTS:
 * - HeaderControls: Navigation tabs, search, and add patient functionality
 * - ConsultationView: Consultation filters, stats, and table display
 * - EditDoctorModal: Modal for editing doctor information
 *
 * HOOKS:
 * - useUserRole: Centralized user role detection and permissions
 * - usePatientData: Patient data fetching and management
 * - useConsultationData: Consultation data and filtering
 * - useAppointmentData: Appointment data management
 * - useDoctorsList: Doctors list management
 * - useDoctorManagement: Doctor editing operations
 * - useAppointmentBooking: Appointment booking functionality
 *
 * All functionality remains intact while improving code organization and maintainability.
 */
function UserReportsInner() {
    console.log("🔥🔥🔥 === COMPONENT MOUNTING/RENDERING ===");

    // Initialize the toast hook
    const toast = useToast();
    const location = useLocation();
    const isFrontdeskView = location.pathname.includes('/frontdesk');

    // Get user role information
    const {
        isOwner,
        isAdmin,
        isDoctor,
        isFrontdesk,
        currentDoctorId,
        clinicId,
    } = useUserRole();



    // Navigation state
    const [consultable, setConsultable] = useState(true);
    const [appoinmentable, setAppoinmentable] = useState(false);
    const [showPatientList, setShowPatientList] = useState(false);
    const [showReports, setShowReports] = useState(false);
    const [showBills, setShowBills] = useState(false);
    // Bill stats state (will be provided to GlobalFiltersBar via HeaderControls)
    const [billStats, setBillStats] = useState(null);
    const [patientSearchQuery, setPatientSearchQuery] = useState("");
    const [ric, setric] = useState("");
    // Track selected clinic for admin flow
    const [selectedClinic, setSelectedClinic] = useState(null);
    // Fallback admin detection from localStorage user object
    const [isAdminFromStorage, setIsAdminFromStorage] = useState(false);

    const [appointmentStats, setAppointmentStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
    });

    useEffect(() => {
        try {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                console.log('🔥 Loaded "user" storage:', parsed);
                if (parsed?.ric || parsed?.role || parsed.role_in_clinic) {
                    setric(
                        parsed?.ric || parsed?.role || parsed.role_in_clinic,
                    );
                    console.log(
                        '🔥 Loaded RIC from "user" storage:',
                        parsed.ric,
                    );
                }

                // Fallback admin detection from stored user object
                if (
                    parsed?.role === "Admin" ||
                    parsed?.role_in_clinic === "Admin" ||
                    parsed?.isAdmin
                ) {
                    setIsAdminFromStorage(true);
                }
            } else {
                // no user in storage
            }
        } catch (err) {
            console.warn('Failed to read "user" from localStorage', err);
        }
    }, []);
    // Use custom hooks for data management
    // Note: useDoctorsList must be called first to provide selectedDoctor to useConsultationData
    const { doctors, selectedDoctor, setSelectedDoctor } = useDoctorsList(
        isOwner,
        isAdmin || isAdminFromStorage,
        isDoctor,
        currentDoctorId,
        selectedClinic || clinicId,
    );

    const {
        patientsData,
        setPatientsData,
        selectedPatient,
        setSelectedPatient,
        filterPatients,
        handleRowClick: handlePatientRowClick,
        refreshPatients,
    } = usePatientData(
        isDoctor,
        isOwner,
        isAdmin,
        isFrontdesk,
        currentDoctorId,
        clinicId,
    );

    // Focus mode - triggered when a consultation is active
    const isConsultationActive = selectedPatient !== null;

    // Patient list now relies on global filters for clinic/doctor/date. Local states removed.

    // Grab global filters early so consultations will react to central bar
    const { filters: globalFilters } = useGlobalFilters();

    const {
        consultationsData,
        fromDate,
        toDate,
        totalAppointments,
        pendingAppointments,
        completedAppointments,
        handleFromDateChange,
        handleToDateChange,
        filterConsultations,
        updateConsultationStatus, // Make sure this is destructured
        fetchConsultationsManually,
    } = useConsultationData(
        isDoctor,
        isOwner,
        isAdmin,
        isFrontdesk, // Pass isFrontdesk here
        currentDoctorId,
        globalFilters && globalFilters.doctorId
            ? String(globalFilters.doctorId)
            : selectedDoctor,
        globalFilters?.fromDate || null,
        globalFilters?.toDate || null,
    );

    // Fetch clinics when component mounts (for admin) and load initial doctors for owner
    useEffect(() => {
        // Legacy patient list clinic/doctor bootstrap removed; now handled by GlobalFiltersProvider
        // This effect intentionally left minimal.
    }, [isAdmin, isAdminFromStorage, isOwner, clinicId, currentDoctorId]);

    // === GLOBAL_FILTERS_APPLY_POINT: PATIENT_LIST ===
    // Use global filters to fetch patient list (clinic + doctor aggregation)
    const gPatientDoctorId = globalFilters?.doctorId ?? null;
    const gPatientClinicId = globalFilters?.clinicId ?? null;

    // useEffect(() => {
    //   if (!showPatientList) return; // only fetch when patient list tab active
    //   (async () => {
    //     try {
    //       const doctorId = gPatientDoctorId; // primitive stable dep
    //       const clinicIdLocal = gPatientClinicId;
    //       // If specific doctor chosen
    //       let url;
    //       if (doctorId) {
    //         url = `/api/patients/getpatientlist?doctor_id=${doctorId}`;
    //       } else {
    //         // Aggregate fetch (fallback endpoint assumed returns all accessible patients). If backend supports filtering by clinic, append.
    //         url = '/api/patients/getpatientlist';
    //         if (clinicIdLocal) {
    //           // Optional: backend may accept clinic param (uncomment if supported)
    //           // url += `?clinic_id=${clinicIdLocal}`;
    //         }
    //       }
    //       const resp = await axiosInstance.get(url);
    //       const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
    //       setPatientsData(list);
    //     } catch (err) {
    //       console.warn('Failed to fetch patients for global filters', err);
    //     }
    //   })();
    // }, [gPatientDoctorId, gPatientClinicId, showPatientList, setPatientsData]);

    // Sync global selected doctor/clinic into local selection used by hooks
    const gDoctorId = globalFilters?.doctorId ?? null;
    const gClinicId = globalFilters?.clinicId ?? null;
    const gFrom = globalFilters?.fromDate ?? null;
    const gTo = globalFilters?.toDate ?? null;

    useEffect(() => {
        // Only run when primitive values change
        if (gDoctorId === undefined && gClinicId === undefined) return;

        console.log("🔥 GLOBAL FILTERS SYNC EFFECT:", { gDoctorId, gClinicId });

        if (typeof setSelectedDoctor === "function") {
            const newVal = gDoctorId ? String(gDoctorId) : "";
            console.log(
                "🔥 Setting local selectedDoctor from globalFilters ->",
                newVal,
            );
            setSelectedDoctor(newVal);
        }

        if ((isAdmin || isOwner) && typeof setSelectedClinic === "function") {
            setSelectedClinic(gClinicId || null);
        }
    }, [
        gDoctorId,
        gClinicId,
        setSelectedDoctor,
        setSelectedClinic,
        isAdmin,
        isOwner,
    ]);

    // When global doctor/clinic/date changes and consultations tab is active, trigger a fetch
    useEffect(() => {
        if (!consultable) return;

        console.log(
            "🔥 GLOBAL FILTERS changed while consultations active, triggering fetch",
            { gDoctorId, gClinicId, gFrom, gTo },
        );
        try {
            fetchConsultationsManually();
        } catch (err) {
            console.error(
                "🔥 Error triggering manual consultation fetch after global filter change",
                err,
            );
        }
    }, [
        gDoctorId,
        gClinicId,
        gFrom,
        gTo,
        consultable,
        fetchConsultationsManually,
    ]);

    // We call useAppointmentData for its internal effects but don't need the returned values here
    useAppointmentData(consultable, fromDate, toDate, currentDoctorId);

    const {
        isEditDoctorModalOpen,
        editDoctorForm,
        editDoctorLoading,
        editDoctorErrors,
        handleEditDoctorInputChange,
        handleEditDoctorOpdTimingChange,
        handleEditDoctorPaymentChange,
        handleEditDoctorServiceChange,
        handleEditDoctorToggleChange,
        addEditDoctorService,
        removeEditDoctorService,
        handleEditDoctorSubmit,
        closeEditDoctorModal,
    } = useDoctorManagement();

    const {
        isBookAppointmentOpen,
        selectedPatientForBooking,
        handleBookAppointment,
        closeBookingModal,
    } = useAppointmentBooking();

    // (Removed stray test useEffect that had no dependency array)

    // Cleanup effect to revert status on component unmount or route change
    // useEffect(() => {
    //   return async  () => {
    //     // If there's a selected patient when component unmounts, revert status to ARRIVED
    //     // BUT only if the status is not COMPLETED (preserve completed consultations)

    //     if (selectedPatient && selectedPatient.appointment_id) {
    //       // Only update status if consultation is not completed
    //       // Fetch the latest appointment status from API (use token & appointment id from localStorage if available)
    //       const token = localStorage.getItem('token');
    //       const apptId = localStorage.getItem(' APPID_CUR') || selectedPatient.appointment_id;
    //       let latestStatus = selectedPatient.status;

    //       try {
    //         const resp = await axiosInstance.get(
    //           `/api/appointment/getAppointmentById?id=${apptId}`,
    //           {
    //             headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    //             maxBodyLength: Infinity,
    //           }
    //         );

    //         // Normalize payload even if API returns { success: false, data: {...} } or other shapes
    //         const raw = resp?.data || {};
    //         const payload = raw?.data ?? raw;

    //         latestStatus = payload.status;
    //         alert(latestStatus)
    //         console.log('🔥 Fetched latest appointment raw response:', resp?.data);
    //         console.log('🔥 Determined latest appointment status:', latestStatus);
    //       } catch (err) {
    //         console.warn('🔥 Failed to fetch appointment status, using selectedPatient.status', err);
    //       }
    //       if (latestStatus === "COMPLETED") {
    //         selectedPatient.status = "COMPLETED"
    //       }

    //       if (latestStatus !== 'COMPLETED') {
    //         console.log('🔥 CLEANUP: Component unmounting, reverting status to ARRIVED for appointment:', selectedPatient.appointment_id);
    //         alert("ARRIVED")
    //         // Use a fire-and-forget approach since component is unmounting
    //         axiosInstance.post('/api/appointment/updateApptStatus', {
    //           id: selectedPatient.appointment_id,
    //           status: 'ARRIVED'
    //         }).catch(error => {
    //           console.error('🔥 CLEANUP ERROR: Failed to revert status on unmount:', error);
    //         });
    //       }
    //     } else if (selectedPatient && selectedPatient.status === 'COMPLETED') {
    //       console.log('🔥 CLEANUP: Preserving COMPLETED status for appointment:', selectedPatient.appointment_id);
    //     }
    //   };
    // }, [selectedPatient]);

    // Navigation handlers
    const handleConsultationsClick = () => {
        console.log("🔥🔥🔥 === DOCTOR CONSULTATIONS BUTTON CLICKED ===");

        // If a consultation is currently open (selectedPatient is set), close it and go back to list
        if (selectedPatient) {
            console.log("🔥 Consultation is open, closing it to return to list...");
            handleBackFromConsultation();
            // We return here because handleBackFromConsultation handles the cleanup and state reset
            // However, we still want to ensure the UI switches to the consultation tab view
            // handleBackFromConsultation sets selectedPatient to null, which will show ConsultationView if consultable is true
        }

        console.log("🔥 Setting consultable to true");
        setConsultable(true);
        setAppoinmentable(false);
        setShowPatientList(false);
        setShowReports(false);
        setShowBills(false);

        // Manually trigger consultation fetch if needed
        console.log("🔥 Triggering manual consultation fetch...");
        fetchConsultationsManually();
        console.log("🔥🔥🔥 === END DOCTOR CONSULTATIONS BUTTON CLICKED ===");
    };

    const handleAppointmentsClick = () => {
        setConsultable(false);
        setAppoinmentable(true);
        setShowPatientList(false);
        setShowReports(false);
        setShowBills(false);
        // Ensure consultation is closed when switching tabs
        if (selectedPatient) setSelectedPatient(null);
    };

    const handlePatientListClick = () => {
        console.log("🔥🔥🔥 === PATIENT LIST BUTTON CLICKED ===");
        setConsultable(false);
        setAppoinmentable(false);
        setShowPatientList(true);
        setShowReports(false);
        setShowBills(false);
        // Ensure consultation is closed when switching tabs
        if (selectedPatient) setSelectedPatient(null);
        console.log("🔥🔥🔥 === END PATIENT LIST BUTTON CLICKED ===");
    };

    const handleReportsClick = () => {
        setConsultable(false);
        setAppoinmentable(false);
        setShowPatientList(false);
        setShowReports(true);
        setShowBills(false);
        // Ensure consultation is closed when switching tabs
        if (selectedPatient) setSelectedPatient(null);
    };

    const handleBillsClick = () => {
        setConsultable(false);
        setAppoinmentable(false);
        setShowPatientList(false);
        setShowReports(false);
        setShowBills(true);
        // Ensure consultation is closed when switching tabs
        if (selectedPatient) setSelectedPatient(null);
    };

    // Enhanced row click handler for consultations
    const handleConsultationRowClick = async (patient) => {
        console.log("🔥🔥🔥 === CONSULTATION ROW CLICKED ===\n");

        // Security Check: Prevent Front Desk from opening consultation
        // Security Check: Prevent Front Desk from opening consultation
        // Also prevent if we are in the frontdesk view (even if user is admin)
        if (isFrontdesk || isFrontdeskView) {
            console.warn("⛔ Security Alert: Front Desk attempted to open consultation");
            // toast({
            //     title: "Access Denied",
            //     description: "Consultations cannot be opened from the Front Desk view.",
            //     status: "error",
            //     duration: 3000,
            //     isClosable: true,
            // });
            return;
        }

        console.log("🔥 Clicked patient data:", patient);
        console.log("🔥 Appointment ID:", patient.appointment_id);
        console.log("🔥 Current status:", patient.status);
        console.log("🔥 selectedDoctor:", selectedDoctor);
        console.log("🔥 patient.doctor_id:", patient.doctor_id);
        console.log(
            "🔥 Setting selectedPatient to trigger PatientDetails mount...",
        );
        setSelectedPatient(patient);

        try {
            // Determine the doctor ID to use for the profile API call
            const doctorIdToUse =
                patient.doctor_id || selectedDoctor || currentDoctorId;

            // Fetch doctor profile to check isParameters and speech_to_text settings
            let doctorProfile = null;
            if (doctorIdToUse) {
                let cachedProfile = null;
                try {
                    const cached = localStorage.getItem("currentDoctorProfile");
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        if (String(parsed.doctor_id) === String(doctorIdToUse)) {
                            console.log("🔥 Using cached doctor profile from localStorage");
                            cachedProfile = parsed;
                        }
                    }
                } catch (e) {
                    console.warn("Error reading cached doctor profile", e);
                }

                if (!cachedProfile) {
                    try {
                        console.log(
                            "🔥 Fetching doctor profile for ID:",
                            doctorIdToUse,
                        );
                        const profileResponse = await axiosInstance.get(
                            `/api/doctor/profile?doctor_id=${doctorIdToUse}`,
                        );
                        doctorProfile = profileResponse.data;
                        console.log("🔥 Doctor profile fetched:", doctorProfile);

                        // Store doctor profile settings in localStorage for use in PatientDetails components
                        localStorage.setItem(
                            "currentDoctorProfile",
                            JSON.stringify({
                                isParameters:
                                    doctorProfile.doctor?.isParameters || 0,
                                speech_to_text:
                                    doctorProfile.doctor?.speech_to_text || 0,
                                doctor_id: doctorIdToUse,
                                doctorProfile: doctorProfile
                            }),
                        );

                        console.log("🔥 Doctor settings:", {
                            isParameters: doctorProfile.doctor?.isParameters,
                            speech_to_text: doctorProfile.doctor?.speech_to_text,
                        });
                    } catch (profileError) {
                        console.warn(
                            "🔥 Failed to fetch doctor profile:",
                            profileError,
                        );
                        // Set default values if profile fetch fails
                        localStorage.setItem(
                            "currentDoctorProfile",
                            JSON.stringify({
                                isParameters: 0,
                                speech_to_text: 0,
                                doctor_id: doctorIdToUse,

                            }),
                        );
                    }
                }
            }

            // Only update status if consultation is not already completed
            if (patient.status !== "COMPLETED") {
                console.log(
                    "🔥 Updating status to IN_PROGRESS for non-completed consultation",
                );

                // First update the status on the server
                const response = await axiosInstance.post(
                    "/api/appointment/updateApptStatus",
                    {
                        id: patient.appointment_id,
                        status: "IN_PROGRESS",
                    },
                );

                console.log("Status update API response:", response.data);

                // Then use the updateConsultationStatus function from the hook
                // This ensures the status is properly updated in the state management
                updateConsultationStatus(patient.appointment_id, "IN_PROGRESS");

                toast({
                    title: "Consultation Started",
                    description: `Consultation for ${patient.name} is now in progress`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                console.log(
                    "🔥 Consultation is already completed, not updating status",
                );
                toast({
                    title: "Viewing Completed Consultation",
                    description: `Viewing completed consultation for ${patient.name}`,
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error updating appointment status:", error);
            toast({
                title: "Error",
                description:
                    error.response?.data?.message ||
                    "Failed to update consultation status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Handler to receive forwarded socket payloads and update consultation state
    const handleAppointmentStatusUpdate = (payload) => {
        console.log(
            "🔥 handleAppointmentStatusUpdate received payload:",
            payload,
        );
        if (!payload || !payload.appointment_id) return;

        try {
            // Prefer hook helper if available
            if (typeof updateConsultationStatus === "function") {
                updateConsultationStatus(
                    payload.appointment_id,
                    payload.status,
                );
            } else {
                // If hook helper missing, attempt best-effort update via fetchConsultationsManually
                console.warn(
                    "updateConsultationStatus not available; triggering manual refresh",
                );
                try {
                    fetchConsultationsManually();
                } catch (err) {
                    console.warn(
                        "Failed to fetch consultations manually:",
                        err,
                    );
                }
            }
        } catch (e) {
            console.warn("Error applying appointment status update:", e);
        }

        console.log(
            "🔥 handleAppointmentStatusUpdate applied for appointment:",
            payload.appointment_id,
        );
    };

    // Enhanced back handler to revert status to ARRIVED
    const handleBackFromConsultation = async () => {
        if (selectedPatient && selectedPatient.appointment_id) {
            // Only update status if consultation is not completed
            // Fetch the latest appointment status from API (use token & appointment id from localStorage if available)
            const token = localStorage.getItem("token");
            const apptId =
                localStorage.getItem(" APPID_CUR") ||
                selectedPatient.appointment_id;
            let latestStatus = selectedPatient.status;

            try {
                const resp = await axiosInstance.get(
                    `/api/appointment/getAppointmentById?id=${apptId}`,
                    {
                        headers: token
                            ? { Authorization: `Bearer ${token}` }
                            : undefined,
                        maxBodyLength: Infinity,
                    },
                );

                // Normalize payload even if API returns { success: false, data: {...} } or other shapes
                const raw = resp?.data || {};
                const payload = raw?.data ?? raw;

                latestStatus = payload.status;
                // alert(latestStatus)
                console.log(
                    "🔥 Fetched latest appointment raw response:",
                    resp?.data,
                );
                console.log(
                    "🔥 Determined latest appointment status:",
                    latestStatus,
                );
            } catch (err) {
                console.warn(
                    "🔥 Failed to fetch appointment status, using selectedPatient.status",
                    err,
                );
            }
            if (latestStatus === "COMPLETED") {
                selectedPatient.status = "COMPLETED";
            }

            if (latestStatus !== "COMPLETED") {
                try {
                    console.log(
                        "🔥 BACK NAVIGATION: Reverting status to ARRIVED for appointment:",
                        selectedPatient.appointment_id,
                    );

                    // Update the status back to ARRIVED on the server
                    const response = await axiosInstance.post(
                        "/api/appointment/updateApptStatus",
                        {
                            id: selectedPatient.appointment_id,
                            status: "ARRIVED",
                        },
                    );

                    console.log(
                        "🔥 BACK NAVIGATION: Status reverted to ARRIVED:",
                        response.data,
                    );

                    // Update the consultation status in the state management
                    updateConsultationStatus(
                        selectedPatient.appointment_id,
                        "ARRIVED",
                    );

                    toast({
                        title: "Consultation Paused",
                        description: `Consultation for ${selectedPatient.name} has been paused`,
                        status: "info",
                        duration: 3000,
                        isClosable: true,
                    });
                } catch (error) {
                    console.error(
                        "🔥 BACK NAVIGATION ERROR: Failed to revert status:",
                        error,
                    );
                    toast({
                        title: "Warning",
                        description:
                            "Failed to update consultation status, but navigation will continue",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } else {
                console.log(
                    "🔥 BACK NAVIGATION: Consultation is completed, not reverting status",
                );
                toast({
                    title: "Viewing Completed",
                    description: `Completed consultation for ${selectedPatient.name} - status preserved`,
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                });
            }
        }

        // Clean up doctor profile data from localStorage
        localStorage.removeItem("currentDoctorProfile");
        localStorage.removeItem("currentPatientFormData")
        localStorage.removeItem("textInputsData")
        localStorage.removeItem("currentConsultationData")
        // localStorage.removeItem("APPID_CUR")
        // if (
        //     typeof window !== "undefined" &&
        //     window.location &&
        //     typeof window.location.reload === "function"
        // ) {
            window.location.reload();
        // }
        // Clear selected patient to go back to consultation list
        setSelectedPatient(null);
    };

    // Enhanced patient added handler
    const handlePatientAdded = () => {
        refreshPatients();
        setShowPatientList(true);
        setConsultable(false);
        setAppoinmentable(false);
        setShowBills(false);
    };

    // Add booking functionality to patients data
    const enhancedPatientsData = patientsData.map((patient) => ({
        ...patient,
        onEmergency: () => handleBookAppointment(patient, true),
        book_appointments: (isEmergency = false) =>
            handleBookAppointment(patient, isEmergency),
    }));

    // Admin detection handled by useUserRole() and isAdminFromStorage; no additional userRole var needed

    const consultationsStats = {
        totalAppointments,
        pendingAppointments,
        completedAppointments,
    };

    return (
        <>
            <>
                <Box w="100%"  pt={isAdmin ? "16" : "4"} bg="blue.50"  borderRadius="md" pb="4" px="4">
                    {/* Header Controls */}
                    <HeaderControls
                        //role in clinic
                        appointmentStats={appointmentStats}
                        appoinmentable={appoinmentable}
                        consultationsStats={consultationsStats}
                        ric={ric}
                        consultable={consultable}
                        showPatientList={showPatientList}
                        showReports={showReports}
                        showBills={showBills}
                        // Pass billStats and current tab (so HeaderControls / GlobalFiltersBar can render billing stats)
                        billStats={billStats}
                        activeTab={showBills ? "Bills" : null}
                        onConsultationsClick={handleConsultationsClick}
                        onAppointmentsClick={handleAppointmentsClick}
                        onPatientListClick={handlePatientListClick}
                        onReportsClick={handleReportsClick}
                        onBillsClick={handleBillsClick}
                        patientSearchQuery={patientSearchQuery}
                        setPatientSearchQuery={setPatientSearchQuery}
                        clinicId={clinicId}
                        isDoctor={isDoctor}
                        isFrontdesk={isFrontdesk}
                        currentDoctorId={currentDoctorId}
                        onPatientAdded={handlePatientAdded}
                        // Focus mode
                        isConsultationActive={isConsultationActive}
                    />

                    {/* Main Content Area */}
                    {appoinmentable ? (
                        <AppointmentTablePage
                            setAppointmentStats={setAppointmentStats}
                            patientSearchQuery={patientSearchQuery}
                        />
                    ) : selectedPatient ? (
                        <Box mt="-70px">
                            <PatientDetails
                                location={{
                                    state: {
                                        patient: selectedPatient,
                                        appointment: {
                                            id: selectedPatient.appointment_id,
                                            doctor_id:
                                                selectedPatient.doctor_id ||
                                                selectedDoctor,
                                            ...selectedPatient.appointment_data,
                                        },
                                    },
                                }}
                                onBack={handleBackFromConsultation}
                            />
                        </Box>
                    ) : consultable ? (
                        <ConsultationView
                            isOwner={isOwner}
                            isAdmin={isAdmin}
                            isDoctor={isDoctor}
                            doctors={doctors}
                            selectedDoctor={selectedDoctor}
                            setSelectedDoctor={setSelectedDoctor}
                            fromDate={fromDate}
                            toDate={toDate}
                            onFromDateChange={handleFromDateChange}
                            onToDateChange={handleToDateChange}
                            totalAppointments={totalAppointments}
                            pendingAppointments={pendingAppointments}
                            completedAppointments={completedAppointments}
                            consultationsData={consultationsData}
                            filterConsultations={filterConsultations}
                            patientSearchQuery={patientSearchQuery}
                            onAppointmentStatusUpdate={
                                handleAppointmentStatusUpdate
                            }
                            onRowClick={handleConsultationRowClick}
                            onClinicSelect={(cId) => setSelectedClinic(cId)}
                        />
                    ) : showPatientList ? (
                        <>
                            {console.log(
                                "🔥🔥🔥 === RENDERING PATIENT LIST (GLOBAL FILTERS) === with",
                                columnsDataComplex,
                                filterPatients(
                                    enhancedPatientsData,
                                    patientSearchQuery,
                                ),
                            ) && null}
                            <ComplexTable
                                columnsData={columnsDataComplex}
                                tableData={filterPatients(
                                    enhancedPatientsData,
                                    patientSearchQuery,
                                )}
                                setTableDataComplex={setPatientsData}
                                onRowClick={handlePatientRowClick}
                            />
                        </>
                    ) : showReports ? (
                        <ModernReports />
                    ) : showBills ? (
                        <BillsPage onStatsUpdate={setBillStats} />
                    ) : null}
                </Box>
            </>

            {/* Edit Doctor Modal */}
            {/* <EditDoctorModal
        isOpen={isEditDoctorModalOpen}
        onClose={closeEditDoctorModal}
        editDoctorForm={editDoctorForm}
        onInputChange={handleEditDoctorInputChange}
        onOpdTimingChange={handleEditDoctorOpdTimingChange}
        onPaymentChange={handleEditDoctorPaymentChange}
        onServiceChange={handleEditDoctorServiceChange}
        onToggleChange={handleEditDoctorToggleChange}
        onAddService={addEditDoctorService}
        onRemoveService={removeEditDoctorService}
        onSubmit={handleEditDoctorSubmit}
        isLoading={editDoctorLoading}
        errors={editDoctorErrors}
        /> */}

            {/* Book Appointment Modal */}
            {selectedPatientForBooking && (
                <BookAppointmentModal
                    isOpen={isBookAppointmentOpen}
                    onClose={closeBookingModal}
                    newAppointmentData={selectedPatientForBooking}
                />
            )}
        </>
    );
}

export default function UserReports() {
    // Wrap inner with provider & global bar
    // SocketProvider ensures a single Socket.IO connection is created per tab
    // and shared by all child components that use the socket.
    return (
        <SocketProvider>
            <GlobalFiltersProvider>
                <UserReportsInner />
            </GlobalFiltersProvider>
        </SocketProvider>
    );
}
